import {
  createLink,
  Link,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import Cantilever from 'assets/cantilever-logo.svg?react'
import { CantileverButton } from 'components/CantileverButton'
import { LucideIcon } from 'components/LucideIcon'
import {
  NotificationPreferencesModal,
  NotificationsDropdown,
} from 'features/forecastNotifications'
import { PermissionsGate } from 'features/permissions'
import { RightSideDrawer } from 'features/rightDrawer'
import { getSearchResultUrl, GlobalSearchBar } from 'features/search'
import React, { useCallback, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoEntry,
  GeckoOrganization,
  GeckoSite,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

import { ConfigSettings } from './configSettings/ConfigSettings'
import { EntryBrowserButton } from './EntryBrowserButton'
import { MenuDrawer } from './MenuDrawer'
import { ProfileAndSettings } from './profileAndSettings/ProfileAndSettings'
import { SiteSelectors } from './siteSelectors/SiteSelectorsTopBar'
import { SelectedTab } from './types'

interface HomebarProps {
  hideSiteSelector?: boolean
  hideSearchBar?: boolean
  hideMenuButton?: boolean
  selectedOrganization: GeckoOrganization | undefined
  organizations: ReadonlyArray<GeckoOrganization>
  selectedSite: GeckoSite | undefined
  sites: ReadonlyArray<GeckoSite>
  sitesForOrg: ReadonlyArray<GeckoSite>
  isLoading: boolean
  hasEverLoaded: boolean
  selectedTab: SelectedTab | undefined
}

const HomebarContainer = styled.div`
  position: relative;
  height: 48px;
  flex: 0 0 auto;
  background-color: ${({ theme }) => theme.forecast.background2};
  box-shadow: rgba(0, 0, 0, 0.3) 0px 0px 0.75rem;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  z-index: 100;
  gap: 1rem;
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 1;
  min-width: 0;
`

const CantileverLogo = styled(Cantilever)`
  height: 32px;
  min-width: 32px;
  max-width: 32px;

  path {
    fill: ${({ theme }) => theme.forecast.tertiaryText};
    fill-opacity: 1;
  }
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  width: 36px;
`

const StyledLogoLinkStyles = styled(Link)`
  display: flex;
  align-items: center;
  height: 32px;
  text-decoration: none;
  color: ${({ theme }) => theme.forecast.defaultGhostButton};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.forecast.secondaryText};

    ${CantileverLogo} path {
      fill: ${({ theme }) => theme.forecast.secondaryText};
    }
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};

    ${CantileverLogo} path {
      fill: ${({ theme }) => theme.forecast.secondaryText};
    }
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.border2};
    outline-offset: -2px;
  }
`

const StyledLogoLink = createLink(StyledLogoLinkStyles)

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  margin: 0 auto;
  padding: 0 1rem;
  min-width: 0;
  overflow: visible;
`

const SearchContainer = styled.div`
  width: 100%;
  flex: 1 1 20rem;
  min-width: 20rem;
  padding-left: clamp(0.5rem, 4rem, 8rem);
  padding-right: clamp(0.5rem, 4rem, 8rem);
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: 2.5rem;
  position: relative;
  z-index: 1;
`

export const Homebar: React.FC<HomebarProps> = ({
  hideSiteSelector = false,
  hideSearchBar = false,
  hideMenuButton = false,
  selectedOrganization,
  organizations,
  selectedSite,
  sites,
  sitesForOrg,
  isLoading,
  hasEverLoaded,
  selectedTab,
}) => {
  const { theme } = useGeckoTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false)
  const [isNotificationPreferencesOpen, setIsNotificationPreferencesOpen] =
    useState(false)

  const toggleMenuDrawer = () => {
    setIsMenuDrawerOpen(!isMenuDrawerOpen)
  }

  const closeMenuDrawer = () => {
    setIsMenuDrawerOpen(false)
  }

  const handleSearchResultClick = useCallback(
    (entry: GeckoEntry) => {
      if (!selectedSite?.id) {
        console.error('Site ID is required to navigate to search results')
        return
      }
      const navInfo = getSearchResultUrl(entry, selectedSite?.id)
      if (navInfo) {
        navigate({
          ...navInfo,
        })
      }
    },
    [navigate, selectedSite?.id]
  )

  return (
    <>
      <HomebarContainer>
        <LeftSection>
          <StyledLogoLink
            to='/home'
            search={{
              [SEARCH_PARAM_SITE_ID]: selectedSite?.id ?? sitesForOrg[0]?.id,
            }}>
            <LogoContainer>
              <CantileverLogo />
            </LogoContainer>
          </StyledLogoLink>
          {!hideMenuButton && (
            <CantileverButton
              onClick={toggleMenuDrawer}
              ghost
              title='Toggle navigation menu'
              aria-label='Toggle navigation menu'
              padding='0.5rem 0.75rem'>
              <LucideIcon name='Menu' size={18} />
              Menu
            </CantileverButton>
          )}

          <SiteSelectors
            selectedOrganization={selectedOrganization}
            organizations={organizations}
            selectedSite={selectedSite}
            sites={sites}
            isLoading={isLoading}
            hasEverLoaded={hasEverLoaded}
            hideSiteSelector={hideSiteSelector}
          />
        </LeftSection>

        <CenterSection>
          {!hideSearchBar && (
            <SearchContainer>
              <GlobalSearchBar
                siteID={selectedSite?.id}
                orgID={selectedOrganization?.id}
                onResultClick={handleSearchResultClick}
                resetSignal={location.pathname}
                containerStyles={{
                  backgroundColor: theme.forecast.background1,
                  borderColor: theme.forecast.border2,
                  borderRadius: '0.375rem',
                  height: '36px',
                  focusBorderColor: theme.forecast.border,
                }}
                inputStyles={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.9375rem',
                  color: theme.forecast.activeText,
                  placeholderColor: theme.forecast.secondaryText,
                }}
              />
            </SearchContainer>
          )}
        </CenterSection>

        <RightSection>
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <EntryBrowserButton />
          </PermissionsGate>
          <ConfigSettings
            orgID={selectedOrganization?.id}
            siteID={selectedSite?.id}
          />
          <NotificationsDropdown />
          <ProfileAndSettings
            onOpenNotificationPreferences={() =>
              setIsNotificationPreferencesOpen(true)
            }
          />
        </RightSection>
      </HomebarContainer>

      <MenuDrawer
        isOpen={isMenuDrawerOpen}
        onClose={closeMenuDrawer}
        siteID={selectedSite?.id}
        orgID={selectedOrganization?.id}
        selectedTab={selectedTab}
      />

      <NotificationPreferencesModal
        isOpen={isNotificationPreferencesOpen}
        onClose={() => setIsNotificationPreferencesOpen(false)}
      />

      <RightSideDrawer siteID={selectedSite?.id} />
    </>
  )
}
